import { useAuth0 } from "@auth0/auth0-react";
import styles from "./addExpensePayee.module.css";
import { TextField, FormControl, InputLabel, Select, MenuItem, Button } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import axios from "axios";
import { toast } from "react-smart-toaster";
import React, { useEffect, useState } from "react";
import NumberFormat from "react-number-format";
import { usePlacesWidget } from "react-google-autocomplete";

const zipRegExp = /\b\d{5}\b/;
const phoneRegExp = /^\(?([0-9]{3})\)?[ ]?([0-9]{3})[-]?([0-9]{4})$/;
const PhoneField = React.forwardRef((props, ref) => {
  return (<NumberFormat
    customInput={TextField}
    prefix=""
    displayType="input"
    type="tel"
    format={"(###) ###-####"}
    {...props}
  />);
});

const schema = yup.object().shape({
  company_name: yup.string().required("This field is Required").max(100, "First name cannot be more than 100 characters"),
  first_name: yup.string().max(50, "First name cannot be more than 50 characters"),
  last_name: yup.string().max(50, "First name cannot be more than 50 characters"),
  email: yup.string().email("Invalid E-mail").max(50, "Email cannot be more than 50 characters"),
  phone: yup.string().matches(phoneRegExp, "Invalid phone number"),
  website: yup.string().max(100, "website cannot be more than 100 characters"),
  address: yup.string().max(100, "Address cannot be more than 100 characters"),
  city: yup.string().max(50, "City cannot be more than 50 characters"),
  state: yup.string(),
  zip: yup.string().matches(zipRegExp, { message: "Please enter a valid zip code", excludeEmptyString: true }),
  category_id: yup.string().required("This field is Required").nullable(),
});

const AddExpensePayee = ({ setAddModalOpen, setLoading, setSnackbarOpen, setSnackbarMsg, setAllData, editData, categoryList }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [stateList, setStateList] = useState([]);

  const { handleSubmit, control, errors, setValue, clearErrors } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {
      state: "",
      city: "",
      category_id: "",
      zip: "",
    },
  });

  const { ref: autocompleteRef } = usePlacesWidget({
    apiKey: process.env.REACT_APP_MAP_API_KEY,
    options: {
      types: ["address"],
      componentRestrictions: { country: "us" },
      fields: ["address_components", "geometry"],
    },
    onPlaceSelected: (place) => {
      let address = "";
      let city = "";
      let state = "";
      let postcode = "";

      for (let component of place.address_components) {
        let componentType = component.types[0];
        switch (componentType) {
          case "street_number": {
            address = component.long_name;
            break;
          }
          case "route": {
            address += " " + component.short_name;
            break;
          }
          case "postal_code": {
            postcode = component.long_name;
            break;
          }
          case "locality":
            city = component.long_name;
            break;
          case "sublocality_level_1":
            city = component.long_name;
            break;
          case "administrative_area_level_3":
            city = component.long_name;
            break;
          case "administrative_area_level_1": {
            state = component.short_name;
            break;
          }
          default:
            break;
        }
      }

      setValue("address", address);
      setValue("city", city);
      setValue("state", state);
      setValue("zip", postcode);

      clearErrors("address");
      clearErrors("city");
      clearErrors("state");
      clearErrors("zip");
    },
  });

  useEffect(() => {
    if (editData) {
      setValue("company_name", editData?.company_name);
      setValue("first_name", editData?.first_name);
      setValue("last_name", editData?.last_name);
      setValue("email", editData?.email);
      setValue("website", editData?.website);
      setValue("address", editData?.address);
      setValue("city", editData?.city);
      setValue("state", editData?.state);
      setValue("zip", editData?.zip);
      setValue("category_id", editData?.category_id);
      if (editData?.phone) {
        setValue("phone", editData?.phone);
      }
    }
  }, [editData, setValue]);

  useEffect(() => {
    async function fetchState() {
      await axios.get(process.env.REACT_APP_PROPERTY_API_URL + "v1/states").then((response) => {
        setStateList(response.data.data);
      }).catch((error) => {
        setStateList([]);
      });
    }
    fetchState();
  }, []);

  const onSubmit = async (data) => {
    setLoading(true);
    setAddModalOpen(false);

    data = { ...data };

    const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

    return axios.post(process.env.REACT_APP_PAYMENT_API_URL + "v1/payees", data, {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    }).then((response) => {
      setSnackbarOpen(true);
      setSnackbarMsg("Payee added successfully.");

      setLoading(false);

      setAllData((prev) => {
        return [response.data.data, ...prev];
      });
    }).catch((error) => {
      if (typeof error.response !== "undefined")
        toast.error(error.response.data.message);
      setLoading(false);
    });
  }

  const onUpdate = async (data) => {
    setLoading(true);
    setAddModalOpen(false);

    const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

    return axios.post(process.env.REACT_APP_PAYMENT_API_URL + "v1/payees/" + editData?.id, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    }).then((response) => {
      setSnackbarOpen(true);
      setSnackbarMsg("Payee updated successfully.");

      setLoading(false);

      setAllData((prev) => {
        return prev?.map((item) => {
          if (item.id === editData?.id) return response.data.data;
          return item;
        });
      });
    }).catch((error) => {
      if (typeof error.response !== "undefined")
        toast.error(error.response.data.message);
      setLoading(false);
    });
  };

  return (<div className={`${styles.AddPropertyRow}`}>
    <form onSubmit={handleSubmit(editData ? onUpdate : onSubmit)}>
      <div className={`${styles.AddPropertyHalfSec}`}>
        <div className={`${styles.AddPropertyHalfSecRow}`}>
          <div className={`${styles.HalfSecFull} FormGroup`}>
            <Controller
              name={`company_name`}
              control={control}
              render={(field) => (<TextField
                onChange={(e) => field.onChange(e)}
                id="outlined-basic"
                label="Company Name*"
                variant="outlined"
                value={field.value}
              />)}
            />
            {errors.company_name && (<p className={`${styles.ErrorM}`}>{errors?.company_name?.message}</p>)}
          </div>

          <div className={`${styles.HalfSecHalf} FormGroup`}>
            <Controller
              name={`first_name`}
              control={control}
              render={(field) => (<TextField
                onChange={(e) => field.onChange(e)}
                id="outlined-basic"
                label="First Name"
                variant="outlined"
                value={field.value}
              />)}
            />
            {errors.first_name && (<p className={`${styles.ErrorM}`}>{errors?.first_name?.message}</p>)}
          </div>

          <div className={`${styles.HalfSecHalf} FormGroup`}>
            <Controller
              name={`last_name`}
              control={control}
              render={(field) => (<TextField
                onChange={(e) => field.onChange(e)}
                id="outlined-basic"
                label="Last Name"
                variant="outlined"
                value={field.value}
              />)}
            />
            {errors.last_name && (<p className={`${styles.ErrorM}`}>{errors?.last_name?.message}</p>)}
          </div>

          <div className={`${styles.HalfSecHalf} FormGroup`}>
            <Controller
              name={`email`}
              control={control}
              render={(field) => (<TextField
                onChange={(e) => field.onChange(e)}
                id="outlined-basic"
                label="Email"
                variant="outlined"
                value={field.value}
              />)}
            />
            {errors.email && (<p className={`${styles.ErrorM}`}>{errors?.email?.message}</p>)}
          </div>

          <div className={`${styles.HalfSecHalf} FormGroup`}>
            <Controller
              name={`phone`}
              control={control}
              render={(field) => (<PhoneField
                value={field.value}
                onChange={(e) => field.onChange(e)}
                id="outlined-basic"
                label="Phone"
                variant="outlined"
              />)}
            />
            {errors.phone && (<p className={`${styles.ErrorM}`}>{errors?.phone?.message}</p>)}
          </div>

          <div className={`${styles.HalfSecHalf} FormGroup`}>
            <FormControl fullWidth>
              <InputLabel id="category-select-label">Category*</InputLabel>
              <Controller
                name="category_id"
                control={control}
                render={(field) => (<Select
                  {...field}
                  labelId="category-select-label"
                  id="category-select"
                  label="Category*"
                  onChange={(e) => field.onChange(e)}
                >
                  {categoryList && categoryList.map((c, i) => (<MenuItem key={i} value={c.id}>{c.title}</MenuItem>))}
                </Select>)}
              />
              {errors.category_id && (<p className={`${styles.ErrorM}`}>{errors.category_id.message}</p>)}
            </FormControl>
          </div>

          <div className={`${styles.HalfSecHalf} FormGroup`}>
            <Controller
              name={`website`}
              control={control}
              render={(field) => (<TextField
                onChange={(e) => field.onChange(e)}
                id="outlined-basic"
                label="Website"
                variant="outlined"
                value={field.value}
              />)}
            />
            {errors.website && (<p className={`${styles.ErrorM}`}>{errors?.website?.message}</p>)}
          </div>
          <div className={`${styles.HalfSecFull} FormGroup`}>
            <Controller
              name={`address`}
              control={control}
              render={(field) => (<TextField
                inputRef={autocompleteRef}
                placeholder=""
                onChange={(e) => field.onChange(e)}
                id="outlined-basic"
                variant="outlined"
                label="Address"
                value={field.value}
                error={errors?.address}
              />)}
            />
            {errors.address && (<p className={`${styles.ErrorM}`}>{errors?.address?.message}</p>)}
          </div>

          <div className={`${styles.HalfSecOneThird} FormGroup`}>
            <Controller
              name={`city`}
              control={control}
              render={(field) => (<TextField
                onChange={(e) => field.onChange(e)}
                id="outlined-basic"
                label="City"
                variant="outlined"
                value={field.value}
              />)}
            />
            {errors.city && (<p className={`${styles.ErrorM}`}>{errors?.city?.message}</p>)}
          </div>

          <div className={`${styles.HalfSecOneThird} FormGroup`}>
            <FormControl>
              <InputLabel id="category-select-label">State</InputLabel>
              <Controller
                name="state"
                control={control}
                render={(field) => (<Select
                  {...field}
                  labelId="state-select-label"
                  id="state-select"
                  label="State"
                  onChange={(e) => field.onChange(e)}
                >
                  {stateList && stateList.map((s) => (<MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>))}
                </Select>)}
              />
              {errors.state && (<p className={`${styles.ErrorM}`}>{errors.state.message}</p>)}
            </FormControl>
          </div>

          <div className={`${styles.HalfSecOneThird} FormGroup`}>
            <Controller
              name={`zip`}
              control={control}
              render={(field) => (<TextField
                onChange={(e) => field.onChange(e)}
                id="outlined-basic"
                label="Zip Code"
                variant="outlined"
                value={field.value}
              />)}
            />
            {errors.zip && (<p className={`${styles.ErrorM}`}>{errors?.zip?.message}</p>)}
          </div>
        </div>
      </div>

      <div className={`${styles.ButtonSec}`}>
        <Button type="button" onClick={(e) => setAddModalOpen(false)} className="CancelPopupBU">Cancel</Button>
        <Button type="submit" className="SubmitPopupBU">{editData ? "Update" : "Add"}</Button>
      </div>
    </form>
  </div>);
};

export default AddExpensePayee;